import React, { useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { MUSIC_VIDEO_EFFECTS } from 'fym-shared/src/music24/musicVideoEffects';
import { TypedMemo } from '../../common/types';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { effectKindMessages, effectMessages } from '../messages/effectMessages';
import type { LaneProps } from './Timeline';
import { effectRows } from './effectRows';
import { formatTime, spanBox } from './timelineGeometry';
import { useEffectDrag } from './useEffectDrag';
import styles from './EffectsLane.module.scss';

const FAMILY_CLASS = {
  lyric: styles.familyLyric,
  title: styles.familyTitle,
  transition: styles.familyTransition,
  shot: styles.familyShot,
} as const;

/**
 * The effects lane (spec 2026-10-17 directed video §8; the look of the text lane): one block per
 * effect at its time, a colour per family, overlapping effects on their own rows. A click selects
 * the effect; a drag moves it on the beat and keeps its length; a drag of its end edge changes the
 * length.
 */
const EffectsLaneComponent: React.FC<LaneProps> = ({ view, snapOn, onGuide }) => {
  const intl = useIntl();
  const { doc, selection, select, transport, readOnly } = useMusicVideoEditor();
  const laneRef = useRef<HTMLDivElement>(null);
  const drag = useEffectDrag({ laneRef, view, snapOn, onGuide });
  const { rows, count } = useMemo(() => effectRows(doc.effects), [doc.effects]);
  const selectedId = selection?.kind === 'effect' ? selection.id : null;
  const rowHeight = 100 / count;
  const ghostBox = drag.ghost === null ? null : spanBox(drag.ghost.startSec, drag.ghost.endSec, view);
  const ghostRow = drag.ghost === null ? 0 : (rows.get(drag.ghost.id) ?? 0);

  return (
    <div ref={laneRef} className={clsx(styles.lane, styles.hEffects)} data-testid="effects-lane">
      {doc.effects.map((effect) => {
        const box = spanBox(effect.startSec, effect.endSec, view);
        if (box === null) {
          return null;
        }
        const name = intl.formatMessage(effectKindMessages[effect.kind]);
        const row = rows.get(effect.id) ?? 0;
        const place = {
          left: `${box.left}%`,
          width: `calc(${box.width}% - 2px)`,
          top: `calc(${row * rowHeight}% + 2px)`,
          height: `calc(${rowHeight}% - 4px)`,
        };
        const isOn = effect.id === selectedId;
        return (
          <React.Fragment key={effect.id}>
            <button
              type="button"
              className={clsx(
                styles.effectBlock,
                FAMILY_CLASS[MUSIC_VIDEO_EFFECTS[effect.kind].family],
                !readOnly && styles.effectMovable,
                isOn && styles.blockOn,
                drag.ghost?.id === effect.id && styles.effectMoving
              )}
              style={place}
              data-effect-id={effect.id}
              data-start={effect.startSec}
              data-end={effect.endSec}
              aria-pressed={isOn}
              aria-label={intl.formatMessage(effectMessages.blockLabel, {
                name,
                from: formatTime(effect.startSec, true),
                to: formatTime(effect.endSec, true),
              })}
              title={readOnly ? name : intl.formatMessage(effectMessages.blockTitle, { name })}
              onPointerDown={(event) => drag.onMoveDown(effect, event)}
              onPointerMove={drag.onPointerMove}
              onPointerUp={drag.onPointerUp}
              onPointerCancel={drag.onPointerCancel}
              onKeyDown={(event) => drag.onKeyDown(effect, event)}
              onClick={() => {
                if (drag.takeDragEnd()) {
                  return;
                }
                select({ kind: 'effect', id: effect.id });
                const now = transport.livePositionSec();
                if (now < effect.startSec || now >= effect.endSec) {
                  transport.seek(effect.startSec);
                }
              }}
            >
              {name}
            </button>
            {!readOnly && isOn && drag.ghost === null && (
              <span
                className={styles.endHandle}
                style={{ left: `calc(${box.left + box.width}% - 6px)`, top: place.top, height: place.height }}
                role="slider"
                aria-label={intl.formatMessage(effectMessages.resizeLabel, { name })}
                aria-valuemin={MUSIC_VIDEO_EFFECTS[effect.kind].minSec}
                aria-valuemax={MUSIC_VIDEO_EFFECTS[effect.kind].maxSec}
                aria-valuenow={Math.round((effect.endSec - effect.startSec) * 10) / 10}
                tabIndex={-1}
                onPointerDown={(event) => drag.onEndDown(effect, event)}
                onPointerMove={drag.onPointerMove}
                onPointerUp={drag.onPointerUp}
                onPointerCancel={drag.onPointerCancel}
              />
            )}
          </React.Fragment>
        );
      })}
      {ghostBox !== null && (
        <div
          className={styles.ghost}
          style={{
            left: `${ghostBox.left}%`,
            width: `calc(${ghostBox.width}% - 2px)`,
            top: `calc(${ghostRow * rowHeight}% + 2px)`,
            height: `calc(${rowHeight}% - 4px)`,
          }}
        />
      )}
    </div>
  );
};

export const EffectsLane = TypedMemo(EffectsLaneComponent);
